import diff_match_patch from 'diff-match-patch';
import { Node as PMNode } from '@tiptap/pm/model';
import { AiChangeDecorationRange } from './ai-change-decoration.extension';

export interface TextChunk {
  text: string;
  from: number;
  to: number;
  textStart: number;
  textEnd: number;
}

export interface ExtractedDocText {
  fullText: string;
  chunks: TextChunk[];
}

export function extractDocTextWithPositions(doc: PMNode): ExtractedDocText {
  let fullText = '';
  const chunks: TextChunk[] = [];

  doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      const textStart = fullText.length;
      fullText += node.text;
      const textEnd = fullText.length;
      chunks.push({
        text: node.text,
        from: pos,
        to: pos + node.nodeSize,
        textStart,
        textEnd,
      });
    } else if (node.isBlock && fullText.length > 0 && !fullText.endsWith('\n')) {
      fullText += '\n';
    }
  });

  return { fullText, chunks };
}

export function extractDocText(doc: PMNode): string {
  return extractDocTextWithPositions(doc).fullText;
}

export function computeDiffHighlightRanges(
  oldText: string,
  newDoc: PMNode,
): AiChangeDecorationRange[] {
  const { fullText: newText, chunks } = extractDocTextWithPositions(newDoc);
  if (!newText || oldText === newText) {
    return [];
  }

  const dmp = new diff_match_patch();
  const diffs = dmp.diff_main(oldText, newText);
  dmp.diff_cleanupSemantic(diffs);

  const ranges: AiChangeDecorationRange[] = [];
  let currentIndex = 0;

  for (const [op, text] of diffs) {
    if (op === diff_match_patch.DIFF_EQUAL) {
      currentIndex += text.length;
    } else if (op === diff_match_patch.DIFF_INSERT) {
      const insertStart = currentIndex;
      const insertEnd = currentIndex + text.length;
      currentIndex += text.length;

      for (const chunk of chunks) {
        const start = Math.max(chunk.textStart, insertStart);
        const end = Math.min(chunk.textEnd, insertEnd);
        if (start < end) {
          ranges.push({
            from: chunk.from + (start - chunk.textStart),
            to: chunk.from + (end - chunk.textStart),
            kind: 'applied',
          });
        }
      }
    }
  }

  return ranges;
}
